import Axios from 'axios';
import { getAuthHeader } from '../utils/authToken';
import { clearSessionExpiry } from '../utils/useSessionExpiry';

/**
 * Thin client for the server's /api/v1/voice routes (AONE-Universe-server api/voice.js).
 * Every call carries the admin JWT the same way the rest of the dashboard does.
 */
const BASE = `${process.env.REACT_APP_URL}/api/v1/voice`;
const headers = () => ({ 'Content-Type': 'application/json', Authorization: getAuthHeader() });

// Expired session → back to sign-in, the same way the dashboard layout does it.
const onError = (err) => {
    if (err?.response?.status === 401) {
        clearSessionExpiry();
        window.location.replace('/signin');
    }
    throw err;
};
const get = (path, params) => Axios.get(`${BASE}${path}`, { headers: headers(), params }).then((r) => r.data, onError);
const post = (path, data) => Axios.post(`${BASE}${path}`, data, { headers: headers() }).then((r) => r.data, onError);
const patch = (path, data) => Axios.patch(`${BASE}${path}`, data, { headers: headers() }).then((r) => r.data, onError);
const put = (path, data) => Axios.put(`${BASE}${path}`, data, { headers: headers() }).then((r) => r.data, onError);

export const voiceApi = {
    /** { permitted, configured, enabled, caller_ids, default_caller_id, admin } — any admin */
    me: () => get('/me'),
    /** { token, identity, ttl, expires_at, caller_ids, default_caller_id, disclosure_enabled } */
    token: () => post('/token', {}),
    /** { call_log, student_candidates? } */
    createCall: (body) => post('/calls', body),
    /** { call_logs, total, page, limit } */
    listCalls: (params) => get('/calls', params),
    getCall: (id) => get(`/calls/${id}`),
    updateCall: (id, body) => patch(`/calls/${id}`, body),
    /** { action, call_log, card } — re-run the failing recording copy / transcription now */
    retry: (id) => post(`/calls/${id}/retry`, {}),
    /** { url, expires_in } — short-lived signed S3 URL */
    recordingUrl: (id) => get(`/calls/${id}/recording`),
    /** { activity, limit } — every admin; cards only */
    activity: (params) => get('/activity', params),
    /** { students } — name or consultation-number search with the numbers on file (permitted admins) */
    searchStudents: (q, limit = 10) => get('/students', { q, limit }),
    /** { number, matches: [{ kind: 'student'|'teacher', ...person, contact_label }] } — who has this number on file (permitted admins) */
    lookupContacts: (number) => get('/contacts/by-phone', { number }),
    /** { teachers: [{ id, uuid, first_name, last_name, subject, phone, country_phone_code, e164 }] } */
    searchTeachers: (q, limit = 10) => get('/teachers', { q, limit }),
    /** { hits: [{ call, matches: [{ chunk_id, start, end, speaker, text, score }] }], searched_calls, embedded_now, pending, answer? } */
    searchCalls: ({ kind, uuid }, body) => post(`/${kind === 'teacher' ? 'teachers' : 'students'}/${uuid}/search`, body),
    /** { closed, status } — the browser could not start this call; close the row instead of leaving it Connecting */
    abandonCall: (id, reason) => post(`/calls/${id}/abandon`, { reason }),
    admins: () => get('/admins'),
    setPermission: (uuid, can_voice_call) => put(`/admins/${uuid}/permission`, { can_voice_call }),
    /** { uuid, missed_calls_staff } — listed in the Missed Calls 담당 지정 / 담당자 연결 menus (managers only) */
    setMissedCallsStaff: (uuid, missed_calls_staff) => put(`/admins/${uuid}/missed-calls-staff`, { missed_calls_staff }),
};

export const errorMessage = (err) =>
    err?.response?.data?.error || err?.message || 'Unknown error';

export default voiceApi;
